import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CalendarIcon, 
  CheckIcon, 
  ClockIcon, 
  XMarkIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, 
  PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { 
  generateAppointmentsChartData,
  formatPtBrNumber,
  formatAppointmentDuration
} from '../../../utils';

ChartJS.register(
  ArcElement, 
  BarElement,
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend
);

const AppointmentsSection = ({ metrics, appointments, isMobile }) => {
  const [selectedTimeInterval, setSelectedTimeInterval] = useState('week');
  const [showFullHistory, setShowFullHistory] = useState(false);
  
  // Dados para o gráfico de agendamentos por dia/semana/mês utilizando o utilitário
  const timeData = generateAppointmentsChartData(selectedTimeInterval);

  // Dados para o gráfico de motivos de cancelamento
  const cancellationData = {
    labels: ['Cliente desmarcou', 'Profissional indisponível', 'Horário reagendado', 'Outros'],
    datasets: [
      {
        data: [40, 20, 25, 15],
        backgroundColor: [
          'rgba(234, 88, 12, 0.7)',  // laranja
          'rgba(239, 68, 68, 0.7)',  // vermelho
          'rgba(59, 130, 246, 0.7)', // azul
          'rgba(107, 114, 128, 0.7)' // cinza
        ],
        borderColor: [
          'rgba(234, 88, 12, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(107, 114, 128, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Opções para os gráficos
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    },
    barPercentage: 0.7,
    categoryPercentage: 0.7,
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    }
  };

  // Limitamos o número de agendamentos para exibição inicial
  const displayedAppointments = showFullHistory 
    ? appointments || [] 
    : (appointments || []).slice(0, 5);
    
  // Estatísticas simuladas/calculadas
  const stats = {
    totalAppointments: metrics?.totalAppointments || 200,
    completedAppointments: Math.round((metrics?.completionRate || 75) * (metrics?.totalAppointments || 200) / 100),
    cancellations: Math.round((metrics?.cancellationRate || 15) * (metrics?.totalAppointments || 200) / 100),
    noShows: Math.round((metrics?.noShowRate || 10) * (metrics?.totalAppointments || 200) / 100),
    rescheduleRate: metrics?.rescheduleRate || 12,
    peakDay: 'Quinta-feira',
    peakHour: '15h - 17h',
    lowDay: 'Segunda-feira',
    lowHour: '8h - 10h',
    averagePerDay: Math.round((metrics?.totalAppointments || 200) / 30),
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Agendamentos e Atendimentos</h2>
      
      {/* Cards de estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Total de Agendamentos</h3>
            <CalendarIcon className="h-5 w-5 text-indigo-500" />
          </div>
          <p className="text-2xl font-bold text-gray-800">{formatPtBrNumber(stats.totalAppointments)}</p>
          <p className="mt-1 text-xs text-gray-500">no período selecionado</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Atendimentos Realizados</h3>
            <CheckIcon className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-800">{formatPtBrNumber(stats.completedAppointments)}</p>
          <p className="mt-1 text-xs text-gray-500">clientes atendidos</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Cancelamentos</h3>
            <XMarkIcon className="h-5 w-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-800">{formatPtBrNumber(stats.cancellations)}</p>
          <p className="mt-1 text-xs text-gray-500">agendamentos cancelados</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Não Compareceram</h3>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-800">{formatPtBrNumber(stats.noShows)}</p>
          <p className="mt-1 text-xs text-gray-500">clientes faltaram</p>
        </motion.div>
      </div>
      
      {/* Gráfico de agendamentos por tempo */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Agendamentos por Período</h3>
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button 
              onClick={() => setSelectedTimeInterval('hour')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                selectedTimeInterval === 'hour' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Por Hora
            </button>
            <button 
              onClick={() => setSelectedTimeInterval('week')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                selectedTimeInterval === 'week' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Por Dia
            </button>
            <button 
              onClick={() => setSelectedTimeInterval('month')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                selectedTimeInterval === 'month' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Por Semana
            </button>
          </div>
        </div>
        
        <div className="h-64">
          <Bar data={timeData} options={barOptions} />
        </div>
        
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-gray-500">Pico de Agendamentos</p>
            <p className="text-sm font-bold">{stats.peakDay}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Horário Mais Popular</p>
            <p className="text-sm font-bold">{stats.peakHour}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Dia Menos Agendado</p>
            <p className="text-sm font-bold">{stats.lowDay}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Média Diária</p>
            <p className="text-sm font-bold">{formatPtBrNumber(stats.averagePerDay)} agendamentos</p>
          </div>
        </div>
      </motion.div>
      
      {/* Gráfico de motivos de cancelamento */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Motivos de Cancelamento</h3>
          <div className="h-64 flex items-center justify-center">
            <Pie data={cancellationData} options={pieOptions} />
          </div>
          <div className="mt-3 text-center">
            <p className="text-xs text-gray-500">Taxa de reagendamento</p>
            <p className="text-sm font-bold">{stats.rescheduleRate}% dos cancelamentos</p>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Sugestões para Otimização</h3>
            <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full font-medium">
              Inteligente
            </span>
          </div>
          
          <div className="space-y-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-start">
                <ClockIcon className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">Redistribuir horários de pico</p>
                  <p className="text-xs text-green-700 mt-1">
                    Ofereça descontos de 10% para agendamentos nas manhãs de segunda-feira
                    para balancear a demanda.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-start">
                <ArrowPathIcon className="h-5 w-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Reduzir taxa de cancelamentos</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Implemente lembretes 24h antes e política de cancelamento com 48h de antecedência.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-start">
                <UserIcon className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Diminuir não comparecimentos</p>
                  <p className="text-xs text-red-700 mt-1">
                    Implemente confirmação por WhatsApp 2h antes e implante sistema de 
                    depósito prévio de 10%.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Histórico de agendamentos */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
      >
        <h3 className="text-lg font-semibold mb-4">Histórico de Agendamentos</h3>
        
        {displayedAppointments.length > 0 ? (
          <div className="space-y-3">
            {displayedAppointments.map((appointment, index) => (
              <div 
                key={appointment.id || index} 
                className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-3 ${
                      appointment.status === 'completed' ? 'bg-green-500' :
                      appointment.status === 'canceled' ? 'bg-orange-500' : 'bg-red-500'
                    }`} />
                    <div>
                      <p className="font-medium text-sm">{appointment.clientName || 'Cliente ' + (index + 1)}</p>
                      <p className="text-xs text-gray-500">{appointment.serviceName || 'Serviço ' + (index + 1)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">{appointment.date || new Date().toLocaleDateString()}</p>
                    <p className={`text-xs ${
                      appointment.status === 'completed' ? 'text-green-600' :
                      appointment.status === 'canceled' ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {appointment.status === 'completed' ? 'Concluído' :
                       appointment.status === 'canceled' ? 'Cancelado' : 'Não compareceu'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {!showFullHistory && appointments?.length > 5 && (
              <button 
                onClick={() => setShowFullHistory(true)}
                className="w-full py-2 text-indigo-600 text-center text-sm font-medium border border-indigo-100 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                Ver todos os {appointments.length} agendamentos
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum agendamento encontrado no período selecionado</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AppointmentsSection; 